import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, CheckCircle2, Loader2, Send } from "lucide-react";
import { AuthLayout, Field } from "@/components/auth/AuthLayout";
import { api, formatApiError } from "@/api/client";
import { ActivityOption, PlanSelect } from "@/components/auth/SignupControls";

export default function Register() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const [orgName, setOrgName] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [activity, setActivity] = useState<string[]>(["course"]);
    const [planInterest, setPlanInterest] = useState<string>(params.get("plan") || "");
    const [message, setMessage] = useState("");
    const [website, setWebsite] = useState(""); // honeypot
    const [err, setErr] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const toggleActivity = (v: string) => {
        setActivity((a) => (a.includes(v) ? a.filter((x) => x !== v) : [...a, v]));
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErr(null); setLoading(true);
        try {
            await api.post("/public/signup", {
                org_name: orgName,
                first_name: firstName,
                last_name: lastName,
                email: email.trim(),
                phone: phone.trim() || undefined,
                activity_types: activity,
                plan_interest: planInterest || undefined,
                message: message.trim() || undefined,
                website: website || undefined, // honeypot
            });
            setSubmitted(true);
        } catch (e: any) {
            setErr(formatApiError(e));
        } finally { setLoading(false); }
    };

    if (submitted) {
        return (
            <AuthLayout title={t("auth.signup.pending_title")}>
                <div className="space-y-4" data-testid="signup-pending-screen">
                    <div className="flex items-start gap-3 rounded-md p-4"
                        style={{ background: "var(--success-bg)", color: "var(--success-fg)" }}>
                        <CheckCircle2 size={22} className="mt-0.5" />
                        <div>
                            <div className="font-semibold">{t("auth.signup.pending_headline")}</div>
                            <div className="text-sm mt-1">{t("auth.signup.pending_body")}</div>
                        </div>
                    </div>
                    <Link to="/login" data-testid="pending-back-to-login" className="inline-block text-sm hover:underline" style={{ color: "var(--accent)" }}>
                        {t("common.back")}
                    </Link>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout title={t("auth.signup.title")}>
            <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>{t("auth.signup.intro")}</p>
            <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
                <Field label={t("auth.signup.org_name")}>
                    <input data-testid="signup-org" required minLength={2} maxLength={255} value={orgName} onChange={(e) => setOrgName(e.target.value)}
                        className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label={t("auth.signup.first_name")}>
                        <input data-testid="signup-firstname" required value={firstName} onChange={(e) => setFirstName(e.target.value)}
                            className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                    </Field>
                    <Field label={t("auth.signup.last_name")}>
                        <input data-testid="signup-lastname" required value={lastName} onChange={(e) => setLastName(e.target.value)}
                            className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                    </Field>
                </div>
                <Field label={t("auth.email")}>
                    <input data-testid="signup-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                </Field>
                <Field label={t("auth.signup.phone")}>
                    <input data-testid="signup-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                        placeholder="+994 55 123 45 67"
                        className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                </Field>
                <div>
                    <div className="text-xs mb-1.5" style={{ color: "var(--text-muted)" }}>{t("auth.signup.activity")}</div>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { code: "course", label: t("auth.signup.activity.course") },
                            { code: "kindergarten", label: t("auth.signup.activity.kindergarten") },
                        ].map((opt) => (
                            <ActivityOption key={opt.code} code={opt.code} label={opt.label} checked={activity.includes(opt.code)} onToggle={() => toggleActivity(opt.code)} />
                        ))}
                    </div>
                </div>
                <div>
                    <div className="text-xs mb-1.5" style={{ color: "var(--text)" }}>{t("auth.signup.plan_interest")}</div>
                    <PlanSelect value={planInterest} onChange={setPlanInterest} placeholder={t("auth.signup.plan_interest.none")}
                        options={[{ value: "basic", label: "Basic" }, { value: "pro", label: "Pro" }, { value: "enterprise", label: "Enterprise" }]} />
                </div>
                <Field label={t("auth.signup.message")}>
                    <textarea data-testid="signup-message" maxLength={2000} rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
                        className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 88 }} />
                </Field>
                {/* Honeypot — visually hidden, real users leave empty */}
                <div aria-hidden="true" style={{ position: "absolute", left: -9999, top: -9999, width: 1, height: 1, overflow: "hidden" }}>
                    <input tabIndex={-1} autoComplete="off" name="website" value={website} onChange={(e) => setWebsite(e.target.value)} />
                </div>
                {err && (
                    <div className="flex items-start gap-2 rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="signup-error">
                        <AlertCircle size={16} className="mt-0.5" />
                        <span>{err}</span>
                    </div>
                )}
                <button data-testid="signup-submit" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    {t("auth.signup.submit")}
                </button>
                <Link data-testid="have-account-link" to="/login" style={{ color: "var(--accent)" }} className="text-sm hover:underline block text-center">
                    {t("auth.already_have_account")}
                </Link>
            </form>
        </AuthLayout>
    );
}
